import { useState, useCallback } from 'react';
import useSWR from 'swr';
import type { CollectionMechanic } from '../../models/collection-mechanic/collection-mechanic.types';
import type { ClickDataResponse } from './use-partner-click.types';
import type { GetUserResponse } from '../../providers/collinson/collinson.types';
import fetcher from '../../utils/fetcher';

const usePartnerClick = (
  merchantId: CollectionMechanic['externalReference'],
  membershipId: GetUserResponse['data']['id'] | null,
  slug: string,
) => {
  const CLICK_PATH = `/api/partner-click?merchantId=${merchantId}&membershipId=${membershipId}`;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<ClickDataResponse | null>(null);
  const [isError, setIsError] = useState<boolean>(false);
  const [newTab, setNewTab] = useState<Window | null>(null);

  const handleClick = useCallback(() => {
    setIsLoading(true);
    if (typeof window !== 'undefined') {
      // chrome during the creation of a new tab required the URL to be specified
      // As a fallback we just provide partner PDP URL
      const tab = window.open(`/retailers/${slug}`, '_blank');
      setNewTab(tab);
    }
  }, [slug]);

  useSWR(isLoading ? CLICK_PATH : null, fetcher, {
    onSuccess: response => {
      setIsLoading(false);
      if (response.data) {
        setData(response.data);
        setIsError(false);
        if (newTab) {
          newTab.location.replace(response.data?.tracking_url as string);
        }
      } else {
        newTab?.close();
        setIsError(true);
      }
    },
    onError: () => {
      newTab?.close();
      setIsError(true);
      setIsLoading(false);
    },
  });

  return { data, isLoading, isError, handleClick, setIsError };
};

export default usePartnerClick;
